import { useEffect, useState } from 'react';
import {
  listOrgs, createOrg, updateOrg, deleteOrg, listProvisions, retryProvision,
  type FirebaseOrg, type FirebaseProvision,
} from '@/api/firebase';

// Google Cloud organisations the platform can create Firebase projects in —
// one per app, straight from the Apps Setup import. Shown below the accounts
// table to technical admins only: the organisation key can create projects
// and mint keys, so it never leaves this screen.

const STEP_LABEL: Record<FirebaseProvision['state'], string> = {
  new: 'creating project',
  project: 'adding Firebase',
  firebase: 'linking Analytics',
  analytics: 'enabling APIs',
  services: 'creating service account',
  sa: 'registering app',
  ready: 'ready',
};

function relTime(ts: number | null): string {
  if (!ts) return '—';
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const EMPTY_FORM = { name: '', parent: '', billing_account: '', analytics_account_id: '', sa_json: '' };

export function FirebaseOrgs({ canManage }: { canManage: boolean }) {
  const [orgs, setOrgs] = useState<FirebaseOrg[]>([]);
  const [runs, setRuns] = useState<FirebaseProvision[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);

  const reload = () => {
    listOrgs().then((d) => setOrgs(d ?? [])).catch(() => setOrgs([]));
    listProvisions().then((d) => setRuns(d ?? [])).catch(() => setRuns([]));
  };
  useEffect(() => { reload(); }, []);

  // Runs advance in the background; poll while any is still moving.
  const inFlight = runs.some((r) => r.state !== 'ready' && !r.error);
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [inFlight]);

  const onAdd = async () => {
    setAdding(true); setAddError(null);
    try {
      await createOrg({
        name: form.name, parent: form.parent, analytics_account_id: form.analytics_account_id, sa_json: form.sa_json,
        billing_account: form.billing_account || undefined,
      });
      setShowAdd(false); setForm(EMPTY_FORM); reload();
    } catch (e: any) {
      setAddError(e?.message ?? 'failed');
    } finally {
      setAdding(false);
    }
  };

  const onFile = (file: File) => {
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, sa_json: String(r.result ?? '') }));
    r.readAsText(file);
  };

  const onRetry = async (id: number) => {
    setRetrying(id);
    try { await retryProvision(id); reload(); } finally { setRetrying(null); }
  };

  if (!canManage) return null;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold">Project factories</h2>
          <p className="text-xs text-content-subtle">
            Google Cloud organisations where "New project in …" on Apps Setup creates a Firebase project per app.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="bg-info hover:bg-info text-white px-3 py-1.5 rounded-md text-sm">
          + Add organisation
        </button>
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Parent</th>
              <th className="text-left p-3">Analytics account</th>
              <th className="text-left p-3">Billing</th>
              <th className="text-left p-3">Service account</th>
              <th className="text-left p-3">Projects</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} className={`border-t border-border ${o.enabled ? '' : 'opacity-60'}`}>
                <td className="p-3">{o.name}{!o.enabled && <span className="ml-2 text-xs text-content-subtle">(disabled)</span>}</td>
                <td className="p-3 font-mono text-xs">{o.parent}</td>
                <td className="p-3 font-mono text-xs">{o.analytics_account_id}</td>
                <td className="p-3 font-mono text-xs">{o.billing_account ?? <span className="text-content-subtle">none (Spark)</span>}</td>
                <td className="p-3 font-mono text-xs">{o.sa_client_email}</td>
                <td className="p-3">
                  <span className={o.projects >= o.max_projects ? 'text-warning' : ''}>{o.projects} / {o.max_projects}</span>
                  {o.projects >= o.max_projects && <span className="ml-2 text-xs text-content-subtle">full</span>}
                </td>
                <td className="p-3 text-right space-x-2">
                  <button
                    onClick={() => {
                      const raw = prompt(`Project limit for ${o.name} (currently ${o.max_projects}). The Apps Setup picker hides the organisation once it is full.`, String(o.max_projects));
                      if (raw === null) return;
                      const n = Number(raw.trim());
                      if (!Number.isInteger(n) || n < 1) { alert('Enter a whole number of at least 1'); return; }
                      updateOrg(o.id, { max_projects: n }).then(reload).catch((e) => alert(String(e?.message ?? e)));
                    }}
                    className="text-content-muted hover:underline text-xs"
                  >
                    Limit
                  </button>
                  <button
                    onClick={() => updateOrg(o.id, { enabled: !o.enabled }).then(reload)}
                    className="text-content-muted hover:underline text-xs"
                  >
                    {o.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => { if (confirm(`Delete organisation ${o.name}? Projects already created keep working.`)) deleteOrg(o.id).then(reload); }}
                    className="text-danger hover:underline text-xs"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {orgs.length === 0 && (
              <tr><td className="p-6 text-center text-content-subtle" colSpan={7}>No organisations yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {runs.length > 0 && (
        <div className="mt-4 bg-surface border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted">
              <tr>
                <th className="text-left p-3">Project</th>
                <th className="text-left p-3">App</th>
                <th className="text-left p-3">Organisation</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Updated</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{r.project_id}</td>
                  <td className="p-3 font-mono text-xs">{r.bundle_id}</td>
                  <td className="p-3">{r.org_name}</td>
                  <td className="p-3">
                    {r.error ? (
                      <span className="text-danger text-xs break-words" title={r.error}>⚠ {r.error}</span>
                    ) : r.state === 'ready' ? (
                      <span className="text-success">✓ ready</span>
                    ) : (
                      <span className="text-content-muted">{STEP_LABEL[r.state]}…</span>
                    )}
                  </td>
                  <td className="p-3">{relTime(r.updated_at)}</td>
                  <td className="p-3 text-right">
                    {r.error && r.state !== 'ready' && (
                      <button
                        disabled={retrying === r.id}
                        onClick={() => onRetry(r.id)}
                        className="text-info hover:underline text-xs disabled:opacity-50"
                      >
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg w-full max-w-lg p-6 max-h-full overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1">Add organisation</h2>
            <p className="text-xs text-content-subtle mb-4">
              The service account needs Project Creator on the parent, Editor on the Analytics account,
              and Billing User on the billing account. See docs/firebase-provision-ops.ru.md.
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="text-sm text-content-muted">Name</span>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm text-content-muted">Parent (organisation or folder)</span>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono"
                  placeholder="folders/123456789012"
                  value={form.parent} onChange={(e) => setForm({ ...form, parent: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm text-content-muted">Google Analytics account id</span>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono"
                  placeholder="123456789"
                  value={form.analytics_account_id} onChange={(e) => setForm({ ...form, analytics_account_id: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm text-content-muted">Billing account (optional)</span>
                <input className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono"
                  placeholder="billingAccounts/XXXXXX-XXXXXX-XXXXXX"
                  value={form.billing_account} onChange={(e) => setForm({ ...form, billing_account: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm text-content-muted">Service Account JSON</span>
                <textarea className="mt-1 w-full border border-border-strong rounded px-3 py-2 font-mono text-xs h-32"
                  value={form.sa_json} onChange={(e) => setForm({ ...form, sa_json: e.target.value })} />
                <input type="file" accept="application/json" className="mt-2 text-xs"
                  onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
              </label>
              {addError && <div className="text-danger text-sm">{addError}</div>}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button onClick={onAdd} disabled={adding}
                className="bg-info hover:bg-info text-white px-4 py-2 rounded text-sm disabled:opacity-50">
                {adding ? 'Testing…' : 'Test & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
